import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FolderRow {
  readonly folderId: string;
  readonly folderCode: string;
  readonly folderName: string;
  readonly folderType: string | null;
  readonly folderPath: string;
}

const FOLDER_TYPES = ['DEPARTMENT', 'REGION', 'VENDOR', 'CAMPAIGN', 'PROJECT', 'AUDIT', 'CUSTOM'];

export function M23FolderManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FolderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [folderCode, setFolderCode] = useState('');
  const [folderName, setFolderName] = useState('');
  const [folderType, setFolderType] = useState('DEPARTMENT');
  const [message, setMessage] = useState<string | null>(null);

  function load(): void {
    apiRequest<{ data: FolderRow[] }>('/v1/document-folders', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  function onCreate(e: FormEvent): void {
    e.preventDefault();
    setMessage(null);
    setError(null);
    apiRequest<{ data: FolderRow }>('/v1/document-folders', {
      method: 'POST',
      token,
      body: { folderCode, folderName, folderType },
    })
      .then(() => { setMessage(tr('m23.folder.msgCreated')); setFolderCode(''); setFolderName(''); load(); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="folder-management">
      <h1>{tr('m23.folder.title')}</h1>
      <form aria-label="folder-create-form" onSubmit={onCreate}>
        <label htmlFor="folderCode">{tr('m23.folder.folderCode')}</label>
        <input id="folderCode" value={folderCode} onChange={(e) => setFolderCode(e.target.value)} />
        <label htmlFor="folderName">{tr('m23.folder.folderName')}</label>
        <input id="folderName" value={folderName} onChange={(e) => setFolderName(e.target.value)} />
        <label htmlFor="folderType">{tr('m23.folder.folderType')}</label>
        <select id="folderType" value={folderType} onChange={(e) => setFolderType(e.target.value)}>
          {FOLDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit" disabled={!folderCode.trim() || !folderName.trim()}>{tr('m23.folder.createFolder')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && (
        <table aria-label="folders-table">
          <thead><tr><th>{tr('m23.folder.colCode')}</th><th>{tr('m23.folder.colName')}</th><th>{tr('m23.folder.colType')}</th><th>{tr('m23.folder.colPath')}</th></tr></thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.folderId}>
                <td>{f.folderCode}</td>
                <td>{f.folderName}</td>
                <td>{f.folderType ?? '—'}</td>
                <td>{f.folderPath}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
